import { useLocation, Link } from 'react-router-dom';
import {
  Command,
  KanbanSquare,
  Users,
  MessageSquare,
  Phone,
  Calendar,
  Receipt,
  BookOpen,
  Bot,
  Settings,
  DollarSign,
  Terminal,
  LogOut,
  X,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import clsx from 'clsx';

type NavItem = {
  label: string;
  href: string;
  icon: typeof Command;
  exact?: boolean;
  badge?: boolean;
};

const navItems: NavItem[] = [
  { label: 'Command',     href: '/app',        icon: Command,       exact: true },
  { label: 'Analytics',  href: '/analytics',  icon: BarChart3 },
  { label: 'Pipeline',    href: '/pipeline',   icon: KanbanSquare },
  { label: 'Clients',     href: '/clients',    icon: Users },
  { label: 'Messages',    href: '/inbox',      icon: MessageSquare },
  { label: 'Phone Calls', href: '/calls',      icon: Phone },
  { label: 'Calendar',    href: '/calendar',   icon: Calendar },
  { label: 'Invoices',    href: '/invoices',   icon: Receipt },
  { label: 'Automation',  href: '/settings',   icon: Bot,           exact: true },
  { label: 'Company',     href: '/settings/company', icon: Settings },
  { label: 'Catalog',     href: '/settings/catalog', icon: BookOpen },
  { label: 'Rates',       href: '/settings/component-rates', icon: DollarSign },
  { label: 'Dev',         href: '/dev',              icon: Terminal },
];

export interface SidebarProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
  userRole?: string;
  onLogout?: () => void;
  onOpenCommand?: () => void;
}

export function Sidebar({ open, onClose, userName = 'User', userRole = 'owner', onLogout, onOpenCommand }: SidebarProps) {
  const location = useLocation();

  function isActive(item: (typeof navItems)[number]) {
    if (item.exact) return location.pathname === item.href;
    return location.pathname.startsWith(item.href);
  }

  return (
    <aside
      className={clsx(
        // Base
        'fixed inset-y-0 left-0 z-40 flex flex-col font-hhp-sans',
        // Width
        'w-[272px]',
        // Background — Happy Home Painter navy
        'border-r border-white/10',
        'bg-hhp-navy',
        // Mobile: slide in/out
        'transition-transform duration-300 ease-in-out',
        open ? 'translate-x-0' : '-translate-x-full',
        // Desktop: always visible
        'lg:relative lg:translate-x-0 lg:z-0',
      )}
    >
      {/* ── Logo ─────────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between p-5 pb-4">
        <div className="flex items-center gap-3.5">
          {/* Logo mark */}
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white p-1.5">
            <img src="/hhp-logo.png" alt="Happy Home Painter" className="h-full w-full object-contain" />
          </div>
          <div>
            <h1 className="font-hhp-display text-lg font-bold text-white leading-none">Happy Home Painter</h1>
            <p className="mt-1 text-[11px] uppercase tracking-[.18em] text-hhp-gold/80">
              Workspace
            </p>
          </div>
        </div>

        {/* Mobile close button */}
        <button
          className="rounded-lg p-1.5 text-white/60 hover:bg-white/5 hover:text-white lg:hidden"
          onClick={onClose}
          aria-label="Close navigation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Office assistant status block ───────────────────────────────────── */}
      <div className="mx-4 mb-4 rounded-2xl border border-white/10 bg-white/5 p-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full bg-hhp-gold" />
            <span className="text-[11px] font-bold uppercase tracking-[.14em] text-white">
              Assistant active
            </span>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
            Safe
          </span>
        </div>
        <p className="mt-2.5 text-[11px] leading-[1.6] text-white/60">
          Handling routine office work. Approval required for scheduling, reputation & money.
        </p>
        {onOpenCommand && (
          <button
            type="button"
            onClick={onOpenCommand}
            className="mt-3 w-full rounded-xl border border-hhp-gold/30 bg-hhp-gold/10 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[.12em] text-hhp-gold transition hover:border-hhp-gold/50 hover:bg-hhp-gold/15"
          >
            Open Activity
          </button>
        )}
      </div>

      {/* ── Navigation ──────────────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-0.5">
        {navItems.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={clsx(
                'flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all duration-150',
                active
                  ? 'bg-hhp-gold/12 text-hhp-gold'
                  : 'text-white/60 hover:bg-white/5 hover:text-white',
              )}
            >
              <item.icon className={clsx('h-5 w-5 shrink-0', active ? 'text-hhp-gold' : 'text-white/40')} />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-400">
                  !
                </span>
              )}
              {active && <ChevronRight className="h-3.5 w-3.5 text-hhp-gold/60" />}
            </Link>
          );
        })}
      </nav>

      {/* ── User footer ─────────────────────────────────────────────────────── */}
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {/* Avatar */}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hhp-gold/20 text-hhp-gold text-xs font-bold uppercase">
              {userName.charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-white">{userName}</p>
              <p className="truncate text-[10px] uppercase tracking-wider text-white/50 capitalize">
                {userRole}
              </p>
            </div>
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="rounded-lg p-1.5 text-white/50 hover:bg-white/5 hover:text-red-400 transition-colors"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
